import { FormEvent, useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import { DirectGoalCostType } from '../../../../../data-layer/direct/types';
import { RUB } from '../../../../../utils/char';
import { composeValidators } from '../../../../../utils/validators/compose';
import { makeIntegerValidator } from '../../../../../utils/validators/makeIntegerValidator';
import { makeRequiredValidator } from '../../../../../utils/validators/makeRequiredValidator';
import { IconChevron } from '../../../../icon/IconChevron';
import { IconClose } from '../../../../icon/IconClose';
import { IconButton } from '../../../../input/IconButton';
import { Input as InputBase } from '../../../../input/Input';
import { makeTextControlValidate } from '../../../../input/makeTextControlValidate';
import { Flex } from '../../../../layout/Flex';
import { Typography } from '../../../../typography/Typography';
import { ContentPromotionDropdown } from '../ContentPromotionDropdown/ContentPromotionDropdown';
import { costTypeOptions } from './constants';
import { Wrapper } from './styles';
import { ContentPromotionBudgetGoalProps } from './types';

const Input = makeTextControlValidate(InputBase);

const validator = composeValidators(
    makeRequiredValidator<string>(),
    makeIntegerValidator<string>(),
);

export const ContentPromotionBudgetGoal: React.FC<ContentPromotionBudgetGoalProps> = ({
    goal,
    value,
    hiddenValue = false,
    showCostType = false,
    costType = DirectGoalCostType.DYNAMIC,
    onRemove,
    onChange,
    onChangeCostType: onChangeCostTypeBase,
    ...other
}) => {
    const goalId = String(goal.id);
    const showCostTypeDropdown =
        costType === DirectGoalCostType.DYNAMIC && !hiddenValue && showCostType;

    const onChangeCostType = useCallback(
        (value: string) => {
            onChangeCostTypeBase(goalId, value as DirectGoalCostType);
        },
        [goalId, onChangeCostTypeBase],
    );

    const endAdornment = useMemo(() => {
        const RubElement = (
            <Typography ml={1} variant="body">
                {RUB}
            </Typography>
        );

        if (showCostType && !showCostTypeDropdown) {
            return (
                <Flex direction="row" spacing={0.5} alignItems="center">
                    {RubElement}
                    <ContentPromotionDropdown
                        value={costType}
                        options={costTypeOptions}
                        onChange={onChangeCostType}
                        trigger={<IconChevron size={16} color="var(--color-primary)" />}
                    />
                </Flex>
            );
        }

        return RubElement;
    }, [costType, onChangeCostType, showCostType, showCostTypeDropdown]);

    return (
        <Wrapper {...other}>
            <Flex>
                <Typography variant="body">{`${goalId} • ${goal.name}`}</Typography>
                <Typography variant="caption" color="secondary">
                    {goal.counterId}
                </Typography>
            </Flex>
            <Flex
                direction="row"
                width="30%"
                minWidth={showCostTypeDropdown ? 210 : 150}
                ml={1}
                justifyContent="flex-end"
                alignItems="center"
            >
                {showCostTypeDropdown ? (
                    <ContentPromotionDropdown
                        value={costType}
                        options={costTypeOptions}
                        onChange={onChangeCostType}
                    />
                ) : null}
                {hiddenValue || showCostTypeDropdown ? null : (
                    <Input
                        key={goalId}
                        type="number"
                        variant="outline"
                        onInput={(event: FormEvent<HTMLInputElement>) =>
                            onChange(goalId, event.currentTarget.value)
                        }
                        endAdornment={endAdornment}
                        value={value ? value.toString() : undefined}
                        min="0"
                        step="1"
                        validator={validator}
                        alignItems="center"
                    />
                )}
                <IconButton pl={2} onClick={() => onRemove(goalId)}>
                    <IconClose />
                </IconButton>
            </Flex>
        </Wrapper>
    );
};
